import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";
import { formatAddress } from "@/lib/address";

type Company = components["schemas"]["CompanyAdminResponse"];

interface ClientCompaniesTableProps {
  clientId: string;
}

// Deliberately separate from ClientOrganisationsTable: companies have no search endpoint or create dialog.
export function ClientCompaniesTable({ clientId }: ClientCompaniesTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });
  const { reset } = pagination;

  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    reset();
  }, [clientId, reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchCompanies() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/companies", {
        params: {
          query: {
            ClientId: clientId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load companies.");
      } else {
        setCompanies(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchCompanies();
    return () => {
      cancelled = true;
    };
  }, [clientId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>ID</TableHead>
              <TableHead>Head office</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {companies.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-muted-foreground h-24 text-center">
                  No companies found.
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell className="font-medium">
                    <Link to={`/companies/${company.id}`} className="hover:underline">
                      {company.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/companies/${company.id}`}>
                      <Badge
                        variant="secondary"
                        className="font-mono text-xs hover:bg-secondary/80"
                        title={company.id}
                      >
                        {company.id?.slice(0, 8)}...
                      </Badge>
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{formatAddress(company) ?? "-"}</TableCell>
                  <TableCell>{formatDateTime(company.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {loading && (
        <div className="flex justify-center py-4">
          <Spinner className="size-5" />
        </div>
      )}

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
